/**
 * The chrome around a comparison page, in five languages.
 *
 * One file rather than five, same reasoning as `tools/ui.ts`: these are a dozen
 * short strings every comparison page needs, and splitting them per language
 * would fetch five chunks to render one page.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

export interface CompareUi {
  readonly bestForHeading: string;
  /** "Choose Fazly if" — the label above `bestForFazly`. */
  readonly chooseFazly: string;
  /** "Choose <rival> if" — `%s` is replaced with the rival's name. */
  readonly chooseRival: string;
  readonly theyDoBetter: string;
  readonly tableHeading: string;
  readonly dimensionColumn: string;
  readonly fazlyColumn: string;
  readonly faqHeading: string;
  readonly otherComparisons: string;
  readonly tryFirst: string;
  /** The label on the button under `tryFirst`, which goes to the tools hub. */
  readonly toolsCta: string;
  readonly indexTitle: string;
  readonly indexDescription: string;
  readonly indexH1: string;
  readonly indexStandfirst: string;
}

export const COMPARE_UI: Record<ContentLang, CompareUi> = {
  en: {
    bestForHeading: "Which one is for you",
    chooseFazly: "Choose Fazly if",
    chooseRival: "Choose %s if",
    theyDoBetter: "What they do better",
    tableHeading: "What Fazly does, dimension by dimension",
    dimensionColumn: "Dimension",
    fazlyColumn: "Fazly",
    faqHeading: "Questions",
    otherComparisons: "Other comparisons",
    tryFirst:
      "Or skip the comparison and measure something — free, no account:",
    toolsCta: "open the free tools",
    indexTitle: "Fazly Compared — Honest Face App Comparisons | Fazly",
    indexDescription:
      "How Fazly compares to UMAX, Qoves, Mirror, LooksMax AI, Cal AI and Aire on the four things that actually differ between face-analysis apps.",
    indexH1: "Fazly compared",
    indexStandfirst:
      "Six comparisons, four dimensions each, and no invented facts about anybody else's product.",
  },
  tr: {
    bestForHeading: "Hangisi sana göre",
    chooseFazly: "Fazly'yi seç, eğer",
    chooseRival: "%s'i seç, eğer",
    theyDoBetter: "Onların daha iyi yaptığı",
    tableHeading: "Fazly ne yapıyor, başlık başlık",
    dimensionColumn: "Başlık",
    fazlyColumn: "Fazly",
    faqHeading: "Sorular",
    otherComparisons: "Diğer karşılaştırmalar",
    tryFirst:
      "Ya da karşılaştırmayı atla ve bir şey ölç — ücretsiz, hesap gerekmiyor:",
    toolsCta: "ücretsiz araçları aç",
    indexTitle:
      "Fazly Karşılaştırmaları — Dürüst Yüz Uygulaması Kıyasları | Fazly",
    indexDescription:
      "Fazly'nin UMAX, Qoves, Mirror, LooksMax AI, Cal AI ve Aire ile yüz analizi uygulamaları arasında gerçekten ayrışan dört başlıkta karşılaştırması.",
    indexH1: "Fazly karşılaştırmaları",
    indexStandfirst:
      "Altı karşılaştırma, her birinde dört başlık ve başkasının ürünü hakkında uydurulmuş tek bir bilgi yok.",
  },
  de: {
    bestForHeading: "Welche für dich",
    chooseFazly: "Nimm Fazly, wenn",
    chooseRival: "Nimm %s, wenn",
    theyDoBetter: "Was die anderen besser machen",
    tableHeading: "Was Fazly tut, Punkt für Punkt",
    dimensionColumn: "Punkt",
    fazlyColumn: "Fazly",
    faqHeading: "Fragen",
    otherComparisons: "Weitere Vergleiche",
    tryFirst:
      "Oder überspring den Vergleich und miss etwas — kostenlos, ohne Konto:",
    toolsCta: "kostenlose werkzeuge öffnen",
    indexTitle: "Fazly im Vergleich — Ehrliche Gesichts-App-Vergleiche | Fazly",
    indexDescription:
      "Wie sich Fazly mit UMAX, Qoves, Mirror, LooksMax AI, Cal AI und Aire bei den vier Punkten vergleicht, die zwischen Gesichtsanalyse-Apps wirklich unterschiedlich sind.",
    indexH1: "Fazly im Vergleich",
    indexStandfirst:
      "Sechs Vergleiche, je vier Punkte, und keine erfundenen Fakten über fremde Produkte.",
  },
  es: {
    bestForHeading: "Cuál es para ti",
    chooseFazly: "Elige Fazly si",
    chooseRival: "Elige %s si",
    theyDoBetter: "Qué hacen mejor",
    tableHeading: "Qué hace Fazly, dimensión por dimensión",
    dimensionColumn: "Dimensión",
    fazlyColumn: "Fazly",
    faqHeading: "Preguntas",
    otherComparisons: "Otras comparaciones",
    tryFirst: "O sáltate la comparación y mide algo: gratis y sin cuenta.",
    toolsCta: "abrir las herramientas gratis",
    indexTitle:
      "Fazly Comparado — Comparativas Honestas de Apps de Cara | Fazly",
    indexDescription:
      "Cómo se compara Fazly con UMAX, Qoves, Mirror, LooksMax AI, Cal AI y Aire en las cuatro cosas que de verdad diferencian a las apps de análisis facial.",
    indexH1: "Fazly comparado",
    indexStandfirst:
      "Seis comparaciones, cuatro dimensiones cada una, y ningún dato inventado sobre el producto de nadie.",
  },
  pt: {
    bestForHeading: "Qual é para você",
    chooseFazly: "Escolha o Fazly se",
    chooseRival: "Escolha o %s se",
    theyDoBetter: "O que eles fazem melhor",
    tableHeading: "O que o Fazly faz, ponto a ponto",
    dimensionColumn: "Ponto",
    fazlyColumn: "Fazly",
    faqHeading: "Perguntas",
    otherComparisons: "Outras comparações",
    tryFirst: "Ou pule a comparação e meça alguma coisa — grátis, sem conta:",
    toolsCta: "abrir as ferramentas grátis",
    indexTitle:
      "Fazly Comparado — Comparações Honestas de Apps de Rosto | Fazly",
    indexDescription:
      "Como o Fazly se compara a UMAX, Qoves, Mirror, LooksMax AI, Cal AI e Aire nas quatro coisas que realmente diferenciam apps de análise facial.",
    indexH1: "Fazly comparado",
    indexStandfirst:
      "Seis comparações, quatro pontos cada, e nenhum dado inventado sobre o produto de outra pessoa.",
  },
};
